import React, { useMemo } from 'react';
import zxcvbn from 'zxcvbn';

/**
 * PasswordStrengthMeter
 *
 * zxcvbn-backed password strength visualisation used in the sign-up form.
 * Renders a 5-segment bar + a short label + the most actionable feedback
 * suggestion. Caller decides whether to gate submission on `score >= 3`.
 *
 * The library runs entirely client-side - it never sends the password
 * anywhere. Bundle cost (~30 KB gzipped) is acceptable for a security
 * surface.
 */
const LABELS = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'];
const BAR_COLORS = ['#e11d48', '#f97316', '#eab308', '#22c55e', '#15803d'];

const PasswordStrengthMeter = ({ password, minScore = 3 }) => {
  const { score, feedback } = useMemo(() => {
    if (!password) return { score: -1, feedback: null };
    const r = zxcvbn(password);
    return { score: r.score, feedback: r.feedback };
  }, [password]);

  if (!password) return null;

  return (
    <div className="mt-2" data-testid="password-strength-meter">
      <div className="flex gap-1" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="h-1.5 flex-1 rounded-full transition-colors duration-200"
            style={{
              backgroundColor: i <= score ? BAR_COLORS[score] : '#e5e7eb',
            }}
          />
        ))}
      </div>
      <div className="mt-1 flex items-center justify-between">
        <span
          className="text-xs font-medium"
          style={{ color: BAR_COLORS[Math.max(score, 0)] }}
          data-testid="password-strength-label"
        >
          {LABELS[Math.max(score, 0)]}
        </span>
        {score < minScore && (
          <span className="text-xs text-gray-500">
            Aim for &quot;Strong&quot; or better
          </span>
        )}
      </div>
      {feedback?.warning && (
        <p className="mt-1 text-xs text-gray-500">{feedback.warning}</p>
      )}
      {!feedback?.warning && feedback?.suggestions?.[0] && (
        <p className="mt-1 text-xs text-gray-500">{feedback.suggestions[0]}</p>
      )}
    </div>
  );
};

export default PasswordStrengthMeter;
